import React from 'react';
import get from 'lodash/get';
import { useRecoilState } from 'recoil';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import Typography from '@material-ui/core/Typography';
import { Breadcrumbs } from 'app/components/Breadcrumb';
import { SwitchButton } from 'app/components/SwitchButton';
import { ExploreSelect } from 'app/components/ExploreSelect';
import { transactionTypes } from 'app/utils/getTransactionTypes';
import { formatMoneyWithPrefix } from 'app/utils/formatMoneyWithPrefix';
import { ModuleHeaderFragmentProps } from 'app/fragments/module-header-fragment';
import {
  selectedFilterAtom,
  SelectedFilterAtomModel,
  SwitchMapState,
  transactionTypeAtom,
} from 'app/state/recoil/atoms';

export function displayPeriod(selectedFilters: SelectedFilterAtomModel) {
  if (selectedFilters.period.length === 1) {
    const startYear = new Date(
      get(selectedFilters.period[0], 'startDate', '')
    ).getFullYear();

    const endYear = new Date(
      get(selectedFilters.period[0], 'endDate', '')
    ).getFullYear();

    if (startYear === endYear) {
      return `for ${startYear}`;
    }
    return `for ${startYear} to ${endYear}`;
  }
  return '';
}

export const ModuleHeaderFragmentLayout = (
  props: ModuleHeaderFragmentProps
) => {
  const [selectedFilters] = useRecoilState(selectedFilterAtom);
  const [transactionType, setTransactionType] =
    useRecoilState(transactionTypeAtom);

  const [exploreMapTable, setExploreMapTable] = useRecoilState(SwitchMapState);

  const selectTransactionTypes = transactionTypes.map((item) => ({
    value: item.code,
    label: item.name,
  }));

  const handleChange = (value: string) => {
    if (value === 'map') {
      setExploreMapTable(true);
    } else if (value === 'table') {
      setExploreMapTable(false);
    }
  };

  const handleTransactionTypeChange = (code: string) => {
    transactionTypes.forEach((item) => {
      if (item.code === code) {
        setTransactionType(item);
      }
    });
  };

  const viewOptions = [
    {
      value: 'map',
      label: (
        <svg width="18" height="17" viewBox="0 0 18 17">
          <path
            d="M16.7917 0.25L16.645 0.2775L11.75 2.175L6.25 0.25L1.08 1.99167C0.8875 2.05583 0.75 2.22083 0.75 2.43167V16.2917C0.75 16.5483 0.951667 16.75 1.20833 16.75L1.355 16.7225L6.25 14.825L11.75 16.75L16.92 15.0083C17.1125 14.9442 17.25 14.7792 17.25 14.5683V0.708333C17.25 0.451667 17.0483 0.25 16.7917 0.25ZM11.75 14.9167L6.25 12.9825V2.08333L11.75 4.0175V14.9167Z"
            fill="#2E4063"
          />
        </svg>
      ),
    },
    {
      value: 'table',
      label: (
        <svg width="18" height="14" viewBox="0 0 18 14">
          <path
            d="M0 0V14H18V0H0ZM5.33 12H2V2H5.33V12ZM10.67 12H7.34V2H10.67V12ZM16 12H12.67V2H16V12Z"
            fill="#2E4063"
          />
        </svg>
      ),
    },
  ];

  return (
    <>
      <Grid item lg={12} spacing={0} container>
        <Box width="100%" height="10px" />
        <Grid item lg={12} container justifyContent="space-between">
          {props.showBreadCrumbs ? (
            <React.Fragment>
              <Grid item xs={9} sm={6} lg={6}>
                {props.breadcrumbs && <Breadcrumbs route={props.breadcrumbs} />}
              </Grid>
              {props.showSwitch && (
                <Hidden lgUp>
                  <Grid
                    item
                    container
                    xs={12}
                    sm={4}
                    lg={3}
                    justifyContent="flex-end"
                  >
                    {props.switchProps && (
                      <SwitchButton {...props.switchProps} />
                    )}
                  </Grid>
                </Hidden>
              )}
            </React.Fragment>
          ) : (
            <React.Fragment>
              {/* ------------------------ */}
              {/* switch button mobile */}
              {props.showSwitch && (
                <Hidden lgUp>
                  <Grid item container xs={9} lg={3} justifyContent="flex-end">
                    {props.switchProps && (
                      <SwitchButton
                        {...props.switchProps}
                        explorePage={props.explorePage}
                      />
                    )}
                  </Grid>
                </Hidden>
              )}

              <Hidden lgUp>
                <Box width="100%" height="15px" />
              </Hidden>

              {/* ------------------------ */}
              {/* activitiy count */}
              <Grid item xs={12} sm={6} lg={3}>
                <Typography variant="h5" color="textPrimary">
                  Global {transactionType.name} {displayPeriod(selectedFilters)}
                </Typography>
                <Typography variant="h2" css="margin-bottom: 0;">
                  {formatMoneyWithPrefix(get(props.geoData, 'count', 0))}
                </Typography>
              </Grid>
            </React.Fragment>
          )}

          {props.showAggregate && (
            <Box
              width="auto"
              display="flex"
              justifyContent="flex-end"
              gridColumnGap="1.5rem"
              gridRowGap="1.5rem"
              // flexDirection={{ lg: 'column' }}
              sx={{
                flexDirection: { xs: 'column', sm: 'row', md: 'row' },
                alignItems: { xs: 'end', sm: 'center', md: 'center' },
              }}
            >
              <div
                css={`
                  display: flex;
                  align-items: center;
                  column-gap: 1rem;
                `}
              >
                Transaction type:
                <ExploreSelect
                  isSingle
                  width="150px"
                  isSearchable={false}
                  options={selectTransactionTypes}
                  placeholder={transactionType.name}
                  defaultValue={{
                    value: transactionType.code,
                    label: transactionType.name,
                  }}
                  onChange={(v) =>
                    handleTransactionTypeChange(v.value as string)
                  }
                />
              </div>
              <div
                css={`
                  display: flex;
                  align-items: center;
                  column-gap: 1rem;
                `}
              >
                <ExploreSelect
                  isSingle
                  width="40px"
                  isSearchable={false}
                  options={viewOptions}
                  defaultValue={viewOptions[0]}
                  placeholder={exploreMapTable ? 'Map' : 'List'}
                  onChange={(v) => handleChange(v.value as string)}
                />
              </div>
            </Box>
          )}

          <Box width="100%" height="24px" />
        </Grid>
      </Grid>
    </>
  );
};
